import { useState } from 'react'
import type { ProjectContextTerm } from '@ai-meeting/shared'
import { XMarkIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'

type Props = {
  term: ProjectContextTerm | null
  disabled?: boolean
  onClose: () => void
  onConfirm: (term: ProjectContextTerm) => Promise<void>
}

/**
 * Confirm removal of a declared dictionary term.
 */
export default function DeleteDictionaryTermModal({
  term,
  disabled = false,
  onClose,
  onConfirm,
}: Props) {
  const { t } = useUiStrings()
  const [submitting, setSubmitting] = useState(false)

  if (!term) return null

  async function handleConfirm() {
    if (!term) return
    setSubmitting(true)
    try {
      await onConfirm(term)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  const pairLabel = term.target
    ? `${term.source} → ${term.target}`
    : term.source

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-dictionary-term-title"
      data-testid="delete-dictionary-term-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
    >
      <div className="flex w-full max-w-md flex-col rounded-2xl bg-[var(--bg-secondary)] shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <h2
            id="delete-dictionary-term-title"
            className="text-lg font-bold text-[var(--text-primary)]"
          >
            {t.projectDetailDictDeleteTitle}
          </h2>
          <button
            type="button"
            aria-label={t.projectDetailDictCloseModal}
            disabled={disabled || submitting}
            onClick={onClose}
            className="rounded-lg p-1 text-[var(--text-tertiary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-5 py-4">
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)]/40 px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
              {t.projectDetailDictDeleteTermHeading}
            </p>
            <p className="mt-1 text-sm font-semibold text-[var(--text-primary)]">{pairLabel}</p>
          </div>
          <p className="text-xs leading-relaxed text-[var(--text-secondary)]">
            {t.projectDetailDictDeleteBody}
          </p>
        </div>

        <div className="flex justify-end gap-2 px-5 pb-5">
          <button
            type="button"
            disabled={disabled || submitting}
            onClick={onClose}
            className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-xs font-semibold text-[var(--text-secondary)]"
          >
            {t.projectDetailContextCancel}
          </button>
          <button
            type="button"
            disabled={disabled || submitting}
            onClick={() => void handleConfirm()}
            className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {t.delete}
          </button>
        </div>
      </div>
    </div>
  )
}
